import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';

const CustomerContext = createContext(null);

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all customers
  const fetchCustomers = useCallback(async (search = '') => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getCustomers(search);
      setCustomers(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching customers:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Get customer by phone (lookup)
  const getCustomerByPhone = async (phone) => {
    try {
      const customer = await api.lookupCustomer(phone);
      return customer;
    } catch (err) {
      // Customer not found is expected, not an error
      if (err.message?.includes('not found')) {
        return null;
      }
      console.error('Error looking up customer:', err);
      return null;
    }
  };

  // Add or update customer (handled by backend during booking)
  const addOrUpdateCustomer = async (customerData) => {
    // This is now handled automatically by the backend when creating bookings
    // This function is kept for compatibility but just refreshes the customer list
    await fetchCustomers();
    return getCustomerByPhone(customerData.phone);
  };

  // Create a new customer
  const createCustomer = async (customerData) => {
    try {
      const newCustomer = await api.createCustomer(customerData);
      setCustomers(prev => [...prev, newCustomer]);
      return newCustomer;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Update a customer
  const updateCustomer = async (id, updates) => {
    try {
      const updatedCustomer = await api.updateCustomer(id, updates);
      setCustomers(prev => prev.map(c => c.id === id ? updatedCustomer : c));
      return updatedCustomer;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Get top customers by total spent
  const getTopCustomers = useCallback((limit = 10) => {
    return [...customers]
      .sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0))
      .slice(0, limit);
  }, [customers]);

  const value = {
    customers,
    loading,
    error,
    fetchCustomers,
    getCustomerByPhone,
    addOrUpdateCustomer,
    createCustomer,
    updateCustomer,
    getTopCustomers
  };

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
};
